import React, { useState, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useTourismData } from '../../context/TourismContext'
import DestinationCard from '../Destination/DestinationCard'
import { formatPrice } from '../../services/api'

function CategoryDetailsMain() {
    const { slug } = useParams()
    const [category, setCategory] = useState(null)
    const [relatedDestinations, setRelatedDestinations] = useState([])
    
    const { 
        categories, 
        destinations,
        loading, 
        errors, 
        fetchCategories,
        fetchDestinations 
    } = useTourismData()

    useEffect(() => {
        fetchCategories()
    }, [fetchCategories])

    // Find category by slug and fetch related destinations
    useEffect(() => {
        if (categories.results && categories.results.length > 0 && slug) {
            const foundCategory = categories.results.find(cat => cat.slug === slug)
            setCategory(foundCategory)
            
            if (foundCategory) {
                fetchDestinations({ category: foundCategory.id })
            }
        }
    }, [categories.results, slug, fetchDestinations])

    // Set related destinations
    useEffect(() => {
        if (destinations.results) {
            setRelatedDestinations(destinations.results)
        }
    }, [destinations.results])

    if (loading.categories) {
        return (
            <section className="space">
                <div className="container">
                    <div className="text-center">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3">Loading category details...</p>
                    </div>
                </div>
            </section>
        )
    }

    if (!category) {
        return (
            <section className="space">
                <div className="container">
                    <div className="alert alert-danger" role="alert">
                        <h4>Category not found</h4>
                        <p>The category you're looking for doesn't exist.</p>
                        <Link to="/activities" className="btn btn-primary">View All Activities</Link>
                    </div>
                </div>
            </section>
        )
    }

    // Mock additional images for gallery (you can replace with real data)
    const categoryImages = [
        category.image,
        "/assets/img/tour/tour_2_1.jpg",
        "/assets/img/destination/destination_4_1.jpg"
    ]

    return (
        <section className="space">
            <div className="container">
                <div className="row">
                    <div className="col-lg-8">
                        {/* Enhanced Category Header with Image Gallery */}
                        <div className="category-hero-enhanced mb-5">
                            <div className="row g-4">
                                {/* Main Large Image */}
                                <div className="col-md-7">
                                    <div className="main-category-image position-relative overflow-hidden rounded-3">
                                        <img 
                                            src={categoryImages[0]} 
                                            alt={category.name}
                                            className="img-fluid w-100"
                                            style={{ 
                                                height: '320px', 
                                                objectFit: 'cover',
                                                transition: 'transform 0.3s ease'
                                            }}
                                            onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                                            onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                                        />
                                        <div className="image-overlay position-absolute top-0 start-0 w-100 h-100 d-flex align-items-end p-4"
                                             style={{ background: 'linear-gradient(transparent 60%, rgba(0,0,0,0.7))' }}>
                                            <div className="text-white">
                                                <span className="badge bg-warning text-dark mb-2 px-3 py-2">
                                                    <i className="fas fa-star me-1"></i>
                                                    Featured Category
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Side Images */}
                                <div className="col-md-5">
                                    <div className="row g-3">
                                        <div className="col-12">
                                            <div className="side-image-container overflow-hidden rounded-3">
                                                <img 
                                                    src={categoryImages[1]} 
                                                    alt={`${category.name} experience`}
                                                    className="img-fluid w-100"
                                                    style={{ height: '150px', objectFit: 'cover' }}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-12">
                                            <div className="side-image-container overflow-hidden rounded-3 position-relative">
                                                <img 
                                                    src={categoryImages[2]} 
                                                    alt={`${category.name} adventure`}
                                                    className="img-fluid w-100"
                                                    style={{ height: '150px', objectFit: 'cover' }}
                                                />
                                                <div className="view-more-overlay position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                                                     style={{ background: 'rgba(0,0,0,0.5)' }}>
                                                    <button className="btn btn-outline-light btn-sm">
                                                        <i className="fas fa-images me-2"></i>
                                                        View Gallery
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Professional Category Information */}
                        <div className="category-info-section mb-5">
                            <div className="row align-items-center">
                                <div className="col-md-8">
                                    <h1 className="display-5 fw-bold text-dark mb-3">{category.name}</h1>
                                    <p className="lead text-muted mb-4 lh-lg">{category.description}</p>
                                    
                                    {/* Enhanced Stats */}
                                    <div className="category-stats d-flex flex-wrap gap-3">
                                        <div className="stat-item bg-light px-4 py-2 rounded-pill">
                                            <i className="fas fa-map-marker-alt text-primary me-2"></i>
                                            <span className="fw-semibold">{relatedDestinations.length}</span> 
                                            <span className="text-muted ms-1">Destinations</span>
                                        </div>
                                        <div className="stat-item bg-light px-4 py-2 rounded-pill">
                                            <i className="fas fa-clock text-success me-2"></i>
                                            <span className="fw-semibold">Year Round</span>
                                            <span className="text-muted ms-1">Available</span>
                                        </div>
                                        <div className="stat-item bg-light px-4 py-2 rounded-pill">
                                            <i className="fas fa-users text-info me-2"></i>
                                            <span className="fw-semibold">All Ages</span>
                                            <span className="text-muted ms-1">Welcome</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-4 text-md-end">
                                    <div className="action-buttons">
                                        <Link to="#tours" className="btn btn-primary btn-lg mb-3 w-100 w-md-auto">
                                            <i className="fas fa-search me-2"></i>
                                            Explore Tours
                                        </Link>
                                        <div className="contact-info text-muted">
                                            <small>
                                                <i className="fas fa-phone me-1"></i>
                                                Need help? Call us
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* What to Expect Section */}
                        <div className="what-to-expect-section mb-5 p-4 bg-light rounded-3">
                            <h3 className="h4 mb-4 text-dark">
                                <i className="fas fa-binoculars text-primary me-2"></i>
                                What to Expect
                            </h3>
                            <div className="row g-4">
                                <div className="col-md-4">
                                    <div className="expectation-item text-center">
                                        <div className="icon-circle bg-primary bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                                             style={{ width: '60px', height: '60px' }}>
                                            <i className="fas fa-paw text-primary fs-4"></i>
                                        </div>
                                        <h5>Wildlife Encounters</h5>
                                        <p className="text-muted small">Close encounters with amazing wildlife in their natural habitat</p>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="expectation-item text-center">
                                        <div className="icon-circle bg-success bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                                             style={{ width: '60px', height: '60px' }}>
                                            <i className="fas fa-camera text-success fs-4"></i>
                                        </div>
                                        <h5>Perfect Photography</h5>
                                        <p className="text-muted small">Capture stunning moments with expert photo guidance</p>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="expectation-item text-center">
                                        <div className="icon-circle bg-warning bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                                             style={{ width: '60px', height: '60px' }}>
                                            <i className="fas fa-award text-warning fs-4"></i>
                                        </div>
                                        <h5>Expert Guides</h5>
                                        <p className="text-muted small">Professional guides with years of experience</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Available Tours */}
                        {relatedDestinations.length > 0 && (
                            <div id="tours" className="related-destinations">
                                <div className="section-header d-flex align-items-center justify-content-between mb-4">
                                    <h3 className="h4 mb-0 text-dark">
                                        Available {category.name} Tours
                                        <span className="badge bg-primary ms-2">{relatedDestinations.length}</span>
                                    </h3>
                                    <div className="view-options">
                                        <button className="btn btn-outline-secondary btn-sm me-2 active">
                                            <i className="fas fa-th-large"></i>
                                        </button>
                                        <button className="btn btn-outline-secondary btn-sm">
                                            <i className="fas fa-list"></i>
                                        </button>
                                    </div>
                                </div>
                                
                                <div className="row gy-4">
                                    {relatedDestinations.slice(0, 6).map((destination) => (
                                        <div key={destination.id} className="col-md-6">
                                            <DestinationCard
                                                destinationID={destination.id}
                                                destinationSlug={destination.slug}
                                                destinationImage={destination.main_image}
                                                destinationTitle={destination.name}
                                                destinationPrice={formatPrice(destination.price)}
                                                destinationCountry={destination.country}
                                                destinationCity={destination.city}
                                                destinationRating={destination.rating}
                                                destinationReviewCount={destination.review_count}
                                                destinationDuration={destination.duration_days}
                                                destinationDifficulty={destination.difficulty}
                                                destinationDescription={destination.short_description}
                                                destinationFeatured={destination.featured}
                                            />
                                        </div>
                                    ))}
                                </div>
                                
                                {relatedDestinations.length > 6 && (
                                    <div className="text-center mt-4">
                                        <button className="btn btn-outline-primary">
                                            Load More Tours <i className="fas fa-chevron-down ms-1"></i>
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        {relatedDestinations.length === 0 && (
                            <div className="no-destinations text-center py-5 bg-light rounded-3">
                                <div className="icon-circle bg-muted bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                                     style={{ width: '80px', height: '80px' }}>
                                    <i className="fas fa-search text-muted fs-2"></i>
                                </div>
                                <h4 className="text-muted">No tours available yet</h4>
                                <p className="text-muted">Check back later for exciting {category.name} tours.</p>
                                <Link to="/activities" className="btn btn-primary">
                                    Explore Other Categories
                                </Link>
                            </div>
                        )}
                    </div>

                    {/* Enhanced Professional Sidebar */}
                    <div className="col-lg-4">
                        <aside className="sidebar-area">
                            {/* Quick Search Widget */}
                            <div className="widget widget_search mb-4">
                                <div className="widget-content bg-white p-4 rounded-3 shadow-sm border">
                                    <h4 className="widget_title text-dark mb-4">
                                        <i className="fas fa-search text-primary me-2"></i>
                                        Quick Search
                                    </h4>
                                    <form className="search-form">
                                        <div className="form-group mb-3">
                                            <input 
                                                type="text" 
                                                className="form-control" 
                                                placeholder="Search destinations..."
                                                style={{ borderRadius: '8px', padding: '12px 16px' }}
                                            />
                                        </div>
                                        <div className="form-group mb-3">
                                            <select className="form-control" style={{ borderRadius: '8px', padding: '12px 16px' }}>
                                                <option>Select Duration</option>
                                                <option>1-3 Days</option>
                                                <option>4-7 Days</option>
                                                <option>1-2 Weeks</option>
                                                <option>2+ Weeks</option>
                                            </select>
                                        </div>
                                        <button type="submit" className="btn btn-primary w-100 py-2">
                                            <i className="fas fa-search me-2"></i>Search Tours
                                        </button>
                                    </form>
                                </div>
                            </div>

                            {/* Other Categories */}
                            <div className="widget widget_categories mb-4">
                                <div className="widget-content bg-white p-4 rounded-3 shadow-sm border">
                                    <h4 className="widget_title text-dark mb-4">
                                        <i className="fas fa-compass text-primary me-2"></i>
                                        Other Adventures
                                    </h4>
                                    <ul className="list-unstyled">
                                        {categories.results && categories.results
                                            .filter(cat => cat.slug !== slug)
                                            .slice(0, 6)
                                            .map((cat) => (
                                            <li key={cat.id} className="mb-3">
                                                <Link 
                                                    to={`/categories/${cat.slug}`}
                                                    className="d-flex align-items-center text-decoration-none p-3 rounded-2 border border-light hover-bg-light transition-all"
                                                    style={{ 
                                                        transition: 'all 0.3s ease',
                                                        ':hover': { backgroundColor: '#f8f9fa', borderColor: '#dee2e6' }
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.target.style.backgroundColor = '#f8f9fa'
                                                        e.target.style.borderColor = '#dee2e6'
                                                        e.target.style.transform = 'translateX(5px)'
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.target.style.backgroundColor = 'transparent'
                                                        e.target.style.borderColor = '#f1f3f4'
                                                        e.target.style.transform = 'translateX(0)'
                                                    }}
                                                >
                                                    <div className="icon-wrapper me-3">
                                                        <i className="fas fa-map-marker-alt text-primary"></i>
                                                    </div>
                                                    <div>
                                                        <h6 className="mb-0 text-dark fw-semibold">{cat.name}</h6>
                                                        <small className="text-muted">Explore adventures</small>
                                                    </div>
                                                    <i className="fas fa-chevron-right text-muted ms-auto"></i>
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                    <div className="text-center mt-3">
                                        <Link to="/activities" className="btn btn-outline-primary btn-sm">
                                            View All Categories
                                        </Link>
                                    </div>
                                </div>
                            </div>

                            {/* Help Widget */}
                            <div className="widget widget_help">
                                <div className="help-widget-content position-relative overflow-hidden rounded-3"
                                     style={{ 
                                         background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                         minHeight: '280px'
                                     }}>
                                    <div className="help-content p-4 text-white position-relative" style={{ zIndex: 2 }}>
                                        <div className="text-center mb-4">
                                            <div className="help-icon bg-white bg-opacity-20 rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                                                 style={{ width: '60px', height: '60px' }}>
                                                <i className="fas fa-headset text-white fs-4"></i>
                                            </div>
                                            <h4 className="text-white mb-2">Need Assistance?</h4>
                                            <p className="text-white-50 small mb-4">
                                                Our travel experts are here to help you plan your perfect adventure
                                            </p>
                                        </div>
                                        
                                        <div className="contact-info text-center mb-4">
                                            <div className="phone-number mb-3">
                                                <i className="fas fa-phone-alt me-2"></i>
                                                <a href="tel:+256214203215" className="text-white text-decoration-none fw-bold fs-5">
                                                    +256 214 203 215
                                                </a>
                                            </div>
                                            <div className="availability text-white-50 small">
                                                <i className="fas fa-clock me-1"></i>
                                                Available 24/7
                                            </div>
                                        </div>

                                        <div className="text-center">
                                            <Link to="/contact" className="btn btn-light btn-sm px-4 py-2 rounded-pill">
                                                <i className="fas fa-comment-dots me-2"></i>
                                                Start Chat
                                            </Link>
                                        </div>
                                    </div>
                                    
                                    {/* Background decoration */}
                                    <div className="position-absolute top-0 end-0 opacity-10" style={{ zIndex: 1 }}>
                                        <i className="fas fa-phone" style={{ fontSize: '120px' }}></i>
                                    </div>
                                </div>
                            </div>

                            {/* Special Offer Widget */}
                            <div className="widget widget_offer mt-4">
                                <div className="offer-widget-content bg-warning bg-opacity-10 border border-warning rounded-3 p-4 text-center">
                                    <div className="offer-icon mb-3">
                                        <i className="fas fa-percent text-warning display-4"></i>
                                    </div>
                                    <h5 className="text-dark mb-2">Limited Time Offer!</h5>
                                    <p className="text-muted small mb-3">
                                        Book your {category.name} adventure today and save up to 25%
                                    </p>
                                    <div className="countdown mb-3">
                                        <small className="text-muted">Offer expires in:</small>
                                        <div className="fw-bold text-warning">5 Days 12:45:30</div>
                                    </div>
                                    <button className="btn btn-warning btn-sm px-4">
                                        <i className="fas fa-tag me-2"></i>
                                        Get Discount
                                    </button>
                                </div>
                            </div>
                        </aside>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default CategoryDetailsMain
